import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { RequestService } from 'src/app/services/request/request.service';
import { ClientSnapshotCacheService } from 'src/app/services/cache/client-snapshot-cache.service';
import { environment } from 'src/environments/environment';

export interface WeatherContext {
  latitude: number;
  longitude: number;
  temperature?: number;
  temperatureApparent?: number;
  conditionCode?: string;
  conditionDescription?: string;
  windSpeed?: number;
  precipitationChance?: number;
  alertSummary?: string;
  updatedAt?: string;
  attributionUrl?: string;
  hourly?: Array<{
    forecastStart?: string;
    temperature?: number;
    conditionCode?: string;
    conditionDescription?: string;
    precipitationChance?: number;
  }>;
}

/**
 * The single weather source for the UI. It deliberately requests location only
 * after a user action; screens can instead provide a known event, lodging, or
 * delivery coordinate.
 */
@Injectable({ providedIn: 'root' })
export class WeatherContextService {
  readonly context$ = new BehaviorSubject<WeatherContext | null>(null);
  readonly loading$ = new BehaviorSubject<boolean>(false);
  readonly error$ = new BehaviorSubject<string | null>(null);
  private readonly cacheTtlMs = 15 * 60 * 1000;
  private readonly locationCache = new Map<string, string>();

  constructor(
    private readonly request: RequestService,
    private readonly cache: ClientSnapshotCacheService,
    private readonly http: HttpClient,
  ) {}

  async refreshCurrentLocation(): Promise<WeatherContext | null> {
    try {
      const coordinates = Capacitor.getPlatform() === 'web'
        ? await this.browserCoordinates()
        : await this.nativeCoordinates();
      return this.loadForCoordinates(coordinates.latitude, coordinates.longitude);
    } catch (error) {
      console.error('Unable to get a location for weather.', error);
      this.error$.next('Allow location access to show local weather.');
      return null;
    }
  }

  async loadForCoordinates(latitude: number, longitude: number, forceRefresh = false): Promise<WeatherContext | null> {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    const roundedLatitude = Number(latitude.toFixed(3));
    const roundedLongitude = Number(longitude.toFixed(3));
    const cacheKey = this.cache.key('weather-context', { latitude: roundedLatitude, longitude: roundedLongitude });

    if (!forceRefresh) {
      const cached = this.cache.get<WeatherContext>(cacheKey, this.cacheTtlMs);
      if (cached) {
        this.context$.next(cached);
        return cached;
      }
    }

    this.loading$.next(true);
    this.error$.next(null);
    try {
      const raw = await this.request.createRequestGet('weather/context', {
        latitude: roundedLatitude,
        longitude: roundedLongitude,
        language: 'en-US',
        countryCode: 'MX',
      });
      const response = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!response?.ok || !response?.weather) throw new Error(response?.message || 'Weather unavailable');
      const weather = response.weather as WeatherContext;
      this.cache.set(cacheKey, weather);
      this.context$.next(weather);
      return weather;
    } catch (error) {
      console.error('Unable to load weather context.', error);
      this.error$.next('Weather is unavailable right now.');
      return null;
    } finally {
      this.loading$.next(false);
    }
  }

  /** Returns only trusted, minimal weather facts suitable for an LIA/Bedrock turn. */
  async assistantContextForMessage(message: string): Promise<Record<string, unknown> | null> {
    if (!this.isWeatherQuestion(message)) return null;

    const weather = await this.refreshCurrentLocation();
    if (!weather) return null;

    return {
      temperature: weather.temperature ?? null,
      temperatureApparent: weather.temperatureApparent ?? null,
      conditionDescription: weather.conditionDescription ?? null,
      windSpeed: weather.windSpeed ?? null,
      precipitationChance: weather.precipitationChance ?? null,
      alertSummary: weather.alertSummary ?? null,
      observedAt: weather.updatedAt ?? null,
    };
  }

  iconName(context: WeatherContext | null): string {
    const code = String(context?.conditionCode || '').toLowerCase();
    if (code.includes('rain') || code.includes('drizzle') || code.includes('shower')) return 'rainy-outline';
    if (code.includes('thunder')) return 'thunderstorm-outline';
    if (code.includes('snow') || code.includes('sleet')) return 'snow-outline';
    if (code.includes('fog') || code.includes('haze')) return 'cloudy-outline';
    if (code.includes('cloud')) return 'partly-sunny-outline';
    return 'sunny-outline';
  }

  conditionLabel(context: WeatherContext | null): string {
    const description = String(context?.conditionDescription || '').trim();
    if (description) return description;

    const code = String(context?.conditionCode || '').toLowerCase();
    if (code.includes('thunder')) return 'Thunderstorms';
    if (code.includes('rain') || code.includes('drizzle') || code.includes('shower')) return 'Rain';
    if (code.includes('snow') || code.includes('sleet')) return 'Snow';
    if (code.includes('fog') || code.includes('haze') || code.includes('smoke')) return 'Hazy';
    if (code.includes('overcast')) return 'Overcast';
    if (code.includes('cloud') || code.includes('mostly')) return 'Partly cloudy';
    if (code.includes('wind')) return 'Windy';
    return code ? 'Sunny' : 'Current conditions';
  }

  /** Resolves coordinates to a city for display; coordinates are never shown in the UI. */
  async cityForWeather(context: WeatherContext | null): Promise<string> {
    if (!context || !Number.isFinite(context.latitude) || !Number.isFinite(context.longitude)) {
      return 'Current location';
    }

    const key = `${context.latitude.toFixed(3)},${context.longitude.toFixed(3)}`;
    const cached = this.locationCache.get(key);
    if (cached) return cached;

    try {
      const response: any = await this.http.get(
        'https://maps.googleapis.com/maps/api/geocode/json',
        { params: { latlng: `${context.latitude},${context.longitude}`, key: environment.ApiKeyGoogleMaps } },
      ).toPromise();
      const components = response?.results?.[0]?.address_components || [];
      const city = components.find((component: any) =>
        component.types?.includes('locality')
        || component.types?.includes('postal_town')
        || component.types?.includes('administrative_area_level_2')
        || component.types?.includes('sublocality')
      )?.long_name;
      const location = city || 'Current location';
      this.locationCache.set(key, location);
      return location;
    } catch (error) {
      console.warn('Unable to resolve weather city.', error);
      return 'Current location';
    }
  }

  private async nativeCoordinates(): Promise<{ latitude: number; longitude: number }> {
    const permission = await Geolocation.requestPermissions();
    if (permission.location !== 'granted') throw new Error('Location permission denied');
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: false,
      timeout: 10_000,
      maximumAge: 10 * 60 * 1000,
    });
    return position.coords;
  }

  private browserCoordinates(): Promise<{ latitude: number; longitude: number }> {
    if (!navigator.geolocation) return Promise.reject(new Error('Browser geolocation is unavailable'));
    return new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(
      position => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      }),
      reject,
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 10 * 60 * 1000 },
    ));
  }

  private isWeatherQuestion(message: string): boolean {
    return /\b(weather|forecast|temperature|rain|raining|storm|wind|umbrella|snow|heat|cold|clima|pron[oó]stico|temperatura|lluvia|llueve|tormenta|viento|paraguas|nieve|calor|fr[ií]o)\b/i.test(message || '');
  }
}
